'use client';

import Icon from '@egalite/ui/Icon';
import CreateProfileBanner from '@egalite/ui/IncluiPCD/CreateProfileBanner';
import { useNotification } from '@egalite/ui/IncluiPCD/NotificationProvider';
import Section from '@egalite/ui/IncluiPCD/Section';
import JobDetail from '@egalite/ui/JobCard/JobDetail';
import Cookies from 'js-cookie';
import Image from 'next/image';
import Link from 'next/link';
import { FC, Fragment, useCallback, useEffect, useState } from 'react';
import { User } from '../../../@types/User';
import { CacheTags } from '../../lib/constants/CacheTags';
import { revalidateTags } from '../../lib/revalidateTags';
import cn from '@egalite/ui/Utils/cn';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import EditDialog from '../CandidateProfile/EditDialog';
import authFetch from '../../lib/auth/authFetch';
import { measureCustomEvent } from '../OpenAIAdsPixel/oaiq';
import ApplyRequirementsDialog, { ApplyRequirement } from './ApplyRequirementsDialog';

type Props = {
	job: any;
	user: User | null;
	appliedJobs: any[] | null;
};

const JobDetails: FC<Props> = ({ job, user, appliedJobs }) => {
	const [isApplied, setIsApplied] = useState(Boolean(appliedJobs?.find(j => j.code === job.code)));
	const [lastContactUpdate, setLastContactUpdate] = useState<Date | null>(null);
	const [showRedirectModal, setShowRedirectModal] = useState(false);
	const [missingRequirements, setMissingRequirements] = useState<ApplyRequirement[]>([]);
	const [showRequirements, setShowRequirements] = useState(false);
	const [applyAfterRequirements, setApplyAfterRequirements] = useState(false);
	const notification = useNotification();
	const router = useRouter();
	const pathname = usePathname();
	const searchParams = useSearchParams();
	// Enquanto um formulário do EditDialog está aberto, o modal de requisitos fica escondido
	const isEditFormOpen = searchParams.get('modal') != null;

	const isSponsor = job?.companyColor && job?.companyImage;
	const isExternalJob = Boolean(job?.applyUrl);

	/** Conversão do ChatGPT Ads: candidatura confirmada, interna ou externa (task #4630). */
	const measureJobApplication = useCallback(() => {
		measureCustomEvent('job_application', {
			contents: [{ id: String(job.code ?? job._id), name: job.name, content_type: 'job' }],
		});
	}, [job._id, job.code, job.name]);

	const updateLastContactUpdate = useCallback(() => {
		authFetch('/api/v2/candidate/updateLastContactUpdate', Cookies.get('token'), {
			method: 'POST',
		})
			.then(res => res.json())
			.then(res => {
				if (res.success) {
					setLastContactUpdate(new Date(res.data.lastContactUpdate));
				}
			});
	}, [setLastContactUpdate]);

	/** Carrega o perfil e o que ainda falta no cadastro para poder se candidatar. */
	const loadProfile = useCallback(() => {
		const token = Cookies.get('token');

		if (!token) {
			return;
		}

		authFetch('/api/v2/candidate/profile', token)
			.then(res => res.json())
			.then(res => {
				if (res.success) {
					setLastContactUpdate(new Date(res.data.lastContactUpdate));
					setMissingRequirements(res.data.missingRequirements ?? []);
				}
			})
			.catch(() => {
				console.error('Não foi possível carregar os dados, tente novamente em alguns minutos.');
			});
	}, []);

	useEffect(() => {
		loadProfile();
	}, [loadProfile]);

	/**
	 * A API é a fonte da verdade dos requisitos: se ela barrar a candidatura, reabrimos o
	 * modal com a lista devolvida na resposta.
	 */
	const handleMissingRequirementsResponse = useCallback((data: any) => {
		if (!Array.isArray(data?.missingRequirements) || data.missingRequirements.length === 0) {
			return false;
		}

		setMissingRequirements(data.missingRequirements);
		setApplyAfterRequirements(true);
		setShowRequirements(true);
		return true;
	}, []);

	/**
	 * Handler para candidatura em vagas externas (ex: Salesforce).
	 * Registra no Konecty e redireciona o candidato para a página externa.
	 */
	const handleExternalApply = useCallback(async () => {
		const token = Cookies.get('token');

		try {
			// Registrar candidatura no Konecty antes de redirecionar
			if (token && user?.type === 'candidate') {
				const result = await fetch(`/api/v2/jobs/${job._id}/apply`, {
					method: 'POST',
					credentials: 'include',
					headers: {
						Authorization: `Bearer ${token}`,
					},
				});
				const data = await result.json();

				if (data.success) {
					setIsApplied(true);
					measureJobApplication();
					revalidateTags([CacheTags['User.AppliedJobs']]);
				}

				// Cadastro incompleto: não redireciona; pede os dados que faltam antes
				if (!data.success && result.status === 400 && handleMissingRequirementsResponse(data)) {
					setShowRedirectModal(false);
					return;
				}
			}
		} catch {
			// Não bloquear o redirecionamento se houver erro no registro
			console.error('Erro ao registrar candidatura interna.');
		}

		// Redirecionar para a página externa
		window.open(job.applyUrl, '_blank', 'noopener,noreferrer');
		setShowRedirectModal(false);
	}, [job._id, job.applyUrl, user, handleMissingRequirementsResponse, measureJobApplication]);

	/** Candidatura interna: cria o Recruitment via API. */
	const submitApplication = useCallback(async () => {
		const token = Cookies.get('token');

		try {
			const result = await fetch(`/api/v2/jobs/${job._id}/apply`, {
				method: 'POST',
				credentials: 'include',
				headers: {
					Authorization: `Bearer ${token}`,
				},
			});
			const data = await result.json();

			if (data.success) {
				notification.success('Candidatura realizada com sucesso!');
				setIsApplied(true);
				measureJobApplication();
				if (new Date(lastContactUpdate) < new Date(Date.now() - 90 * 24 * 60 * 60 * 1000)) {
					router.push(pathname + '?modal=contact&redirectOnClose=/candidato/curriculo');

					updateLastContactUpdate();
				}
				revalidateTags([CacheTags['User.AppliedJobs']]);
			}

			if (!data.success && result.status === 400 && !handleMissingRequirementsResponse(data)) {
				notification.error(data.message);
			}
		} catch (error) {
			notification.error('Ocorreu um erro ao se candidatar à vaga. Tente novamente mais tarde.');
		}
	}, [
		job._id,
		lastContactUpdate,
		notification,
		pathname,
		router,
		updateLastContactUpdate,
		handleMissingRequirementsResponse,
		measureJobApplication,
	]);

	/** Ponto de entrada do botão "Quero me candidatar". */
	const handleApply = useCallback(async () => {
		if (missingRequirements.length > 0) {
			setApplyAfterRequirements(true);
			setShowRequirements(true);
			return;
		}

		if (isExternalJob) {
			// Fluxo de vaga externa: mostrar modal de redirecionamento
			setShowRedirectModal(true);
			return;
		}

		await submitApplication();
	}, [missingRequirements, isExternalJob, submitApplication]);

	// Última pendência resolvida: conclui a candidatura que ficou pendente
	useEffect(() => {
		if (!applyAfterRequirements || missingRequirements.length > 0 || isEditFormOpen) {
			return;
		}

		setApplyAfterRequirements(false);
		setShowRequirements(false);

		if (isExternalJob) {
			setShowRedirectModal(true);
			return;
		}

		submitApplication();
	}, [applyAfterRequirements, missingRequirements, isEditFormOpen, isExternalJob, submitApplication]);

	return (
		<Fragment>
			<EditDialog
				fetchCandidate={loadProfile}
				message={isEditFormOpen && searchParams.get('modal') === 'contact' ? 'Confira seus dados de contato!' : null}
			/>

			{showRequirements && !isEditFormOpen && (
				<ApplyRequirementsDialog
					missingRequirements={missingRequirements}
					onFill={form => router.push(`${pathname}?modal=${form}${form === 'register' ? '&closable=1' : ''}`)}
					onClose={() => {
						setShowRequirements(false);
						setApplyAfterRequirements(false);
					}}
				/>
			)}
			<div className="container mb-36">
				<button
					onClick={() => router.back()}
					className="md:hidden w-max text-base font-bold flex gap-4 text-brand-main mr-auto my-8 ml-4"
				>
					<Icon name="arrow-left" /> Voltar
				</button>
				{isSponsor && (
					<div
						className="h-48 w-full rounded-4xl md:mt-24"
						style={{
							backgroundColor: job?.companyColor,
						}}
					></div>
				)}
				<div
					className={cn('flex flex-col md:flex-row items-start gap-14 lg:gap-28 w-full px-0 md:pr-16 lg:px-28', {
						'justify-start': isSponsor,
						'justify-center': !isSponsor,
					})}
				>
					{isSponsor && (
						<div className="h-[115px] w-[115px] lg:h-[260px] lg:w-[260px] bg-white rounded-2xl p-4 flex grow-0 shrink-0 items-center justify-center ml-8 -mt-40 shadow">
							<Image src={job?.companyImage} width={160} height={160} alt="" />
						</div>
					)}
					<div className="w-full max-w-[750px] mt-0 md:mt-8 px-4 md:px-0">
						<button
							onClick={() => router.back()}
							className="hidden md:flex w-max text-base font-bold gap-4 text-brand-main mr-auto mb-24"
						>
							<Icon name="arrow-left" /> Voltar
						</button>

						<JobDetail
							title={job.name}
							id={job._id}
							code={job.code}
							companyName={job.company?.name?.full}
							address={job.location}
							characteristics={(job.characteristics || '')
								.split(',')
								.map(c => c.trim())
								.join(', ')}
							benefits={job.benefits}
							workHours={job.workHours}
							education={job.education}
							salary={job.wage}
						description={job.description}
						knowledges={
								job.knowledges != null
									? Object.entries(job.knowledges).reduce(
											(acc, [key, value]: [string, any]) => ({
												...acc,
												[key]:
													value != null
														? {
																...value,
																description: value.description,
														  }
														: null,
											}),
											{},
									  )
									: null
							}
							className="pb-8 sm:pb-0"
							isLoggedIn={user && user?.type === 'candidate'}
							isApplied={isApplied}
							onApply={handleApply}
						/>
					</div>
				</div>
				{/* <div className="mt-36 md:mt-48">{!user && <CreateProfileBanner />}</div> */}
			</div>

			{/* Modal de redirecionamento para vagas externas */}
			{showRedirectModal && (
				<div
					className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm"
					onClick={() => setShowRedirectModal(false)}
				>
					<div
						className="bg-white rounded-2xl p-8 md:p-12 max-w-2xl w-full mx-4 shadow-xl"
						onClick={e => e.stopPropagation()}
					>
						<div className="flex items-center gap-3 mb-6">
							<h3 className="text-3xl font-bold text-gray-900">Candidatura Externa</h3>
						</div>

						<p className="text-gray-600 text-xl leading-relaxed mb-8">
							Para continuar a sua candidatura, você será redirecionado à página de carreiras do Salesforce.
							Clique em <strong>Apply Now</strong> e preencha as informações necessárias.
						</p>

						<div className="flex flex-col sm:flex-row gap-3">
							<button
								onClick={() => setShowRedirectModal(false)}
								className="flex-1 px-6 py-4 border border-gray-300 rounded-xl text-lg text-gray-700 font-medium hover:bg-gray-50 transition-colors"
							>
								Cancelar
							</button>
							<button
								onClick={handleExternalApply}
								className="flex-1 px-6 py-4 bg-brand-main text-white rounded-xl text-lg font-medium hover:opacity-90 transition-opacity flex items-center justify-center gap-2"
							>
								Continuar
							</button>
						</div>
					</div>
				</div>
			)}
		</Fragment>
	);
};

export default JobDetails;

